// frontend/src/pages/Transactions.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import api from "../api/api";
import Currency from "../components/Currency";
import { toast } from "react-toastify";
import { Search, Filter } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Transactions() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const errorShownRef = useRef(false);
  const navigate = useNavigate();

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await api.get("/transactions/me");

      const data = Array.isArray(res.data)
        ? res.data
        : res.data?.transactions || res.data?.data || [];

      data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setTransactions(data);
      errorShownRef.current = false;
    } catch (err) {
      console.error("Failed to load transactions:", err);

      if (!errorShownRef.current) {
        errorShownRef.current = true;

        if (err.response?.status === 401) {
          toast.error("Session expired. Please log in again.");
          navigate("/login");
        } else {
          toast.error("Unable to load transaction history.");
        }
      }

      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  /* ==========================
     FIXED STATS – now counts debit + purchase + rebuy as buys
     Subtracts refunds from total spent
  =========================== */
  const totalNumbersUsed = useMemo(
    () =>
      transactions.filter((t) =>
        ["debit", "purchase", "buy", "rebuy"].includes(t.type)
      ).length,
    [transactions]
  );

  const totalSpent = useMemo(
    () =>
      transactions.reduce((sum, t) => {
        const amt = Math.abs(Number(t.amount || 0));

        if (["debit", "purchase", "buy", "rebuy"].includes(t.type)) {
          return sum + amt; // add buys
        }
        if (t.type === "refund") {
          return sum - amt; // subtract refunds
        }
        return sum;
      }, 0),
    [transactions]
  );

  const mostUsedService = useMemo(() => {
    if (!transactions.length) return "None";
    const count = {};
    transactions.forEach((t) => {
      if (["debit", "purchase", "buy", "rebuy"].includes(t.type)) {
        const service = t.service || t.meta?.purpose || t.meta?.service || "Unknown";
        count[service] = (count[service] || 0) + 1;
      }
    });
    return Object.entries(count).sort((a, b) => b[1] - a[1])?.[0]?.[0] || "None";
  }, [transactions]);

  /* ==========================
     FILTERING (unchanged)
  =========================== */
  const filteredTransactions = useMemo(() => {
    let result = [...transactions];

    if (typeFilter !== "all") {
      result = result.filter((t) => t.type === typeFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((t) =>
        [
          t.number,
          t.service,
          t.reference,
          t.type,
          t.meta?.purpose,
          t.meta?.reason,
          t.meta?.activationId,
          t.meta?.counterpartyUsername,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }

    return result;
  }, [transactions, searchQuery, typeFilter]);

  /* ==========================
     HELPERS (unchanged)
  =========================== */
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  const getMessagesCount = (t) => (t.otpReceived ? 1 : 0);
  const getServiceName = (t) =>
    t.type === "transfer_out" || t.type === "transfer_in"
      ? "Wallet transfer"
      : t.meta?.kind === "funding_bonus"
      ? "funding bonus (" + t.meta.percent + "%)"
      : t.service || t.meta?.purpose || "—";
  const getNumberValue = (t) =>
    t.type === "transfer_out" || t.type === "transfer_in"
      ? `${t.type === "transfer_out" ? "To" : "From"} @${t.meta?.counterpartyUsername || "user"}`
      : t.number || t.meta?.number || "—";
  const getAmountValue = (t) => Math.abs(Number(t.amount || 0));

     const getStatusLabel = (t) =>
       t.status === "pending" ? "Pending" : t.status === "failed" ? "Failed" :
    t.type === "fund"
      ? "Funded"
      : t.type === "debit" || t.type === "purchase" || t.type === "buy"
      ? "Purchased"
      : t.type === "rebuy"
      ? "Rebuy"
      : t.type === "refund"
      ? "Refunded"
      : t.type === "credit"
      ? "Bonus"
      : t.type === "transfer_out"
      ? "Sent"
      : t.type === "transfer_in"
      ? "Received"
      : "Completed";

     const getStatusBadge = (type, status) => {
       if (status === "pending") return "bg-yellow-50 text-yellow-700 border-yellow-200";
       if (status === "failed") return "bg-gray-100 text-gray-600 border-gray-300";
    const map = {
      fund: "bg-green-50 text-green-700 border-green-200",
      debit: "bg-orange-50 text-orange-700 border-orange-200",
      purchase: "bg-orange-50 text-orange-700 border-orange-200",
      buy: "bg-orange-50 text-orange-700 border-orange-200",
      rebuy: "bg-orange-50 text-orange-700 border-orange-200",
      refund: "bg-blue-50 text-blue-700 border-blue-200",
      credit: "bg-amber-50 text-amber-700 border-amber-200",
      transfer_out: "bg-red-50 text-red-700 border-red-200",
      transfer_in: "bg-green-50 text-green-700 border-green-200",
    };
    return map[type] || "bg-gray-50 text-gray-700 border-gray-200";
  };

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
            Usage History
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            View all your previously used numbers and messages
          </p>
        </div>

       

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Total Numbers Used
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {loading ? "—" : totalNumbersUsed}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Total Spent
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white">
              {loading ? "—" : <Currency amount={totalSpent} />}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Most Used Service
            </p>
            <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
              {loading ? "—" : mostUsedService}
            </p>
          </div>
        </div>

        {/* Search + Filter – unchanged */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="flex-1 relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Search size={18} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by number, service or reference..."
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 text-gray-900 dark:text-white"
            />
          </div>
          <button
            onClick={() => setShowFilter((prev) => !prev)}
            className="flex items-center justify-center gap-2 px-5 py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition w-full md:w-auto"
          >
            <Filter size={18} />
            <span className="font-medium">Filter</span>
          </button>
        </div>

        {/* Filter Dropdown – unchanged */}
        {showFilter && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 mb-6">
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
              Transaction Type
            </p>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl outline-none focus:ring-2 focus:ring-purple-400 focus:border-purple-400 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
            >
              <option value="all">All Transactions</option>
              <option value="fund">Fund</option>
              <option value="debit">Debit (Buy)</option>
              <option value="purchase">Purchase</option>
              <option value="buy">Buy (old)</option>
              <option value="rebuy">Rebuy</option>
              <option value="refund">Refund</option>
              <option value="credit">Bonus / Credit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="transfer_out">Transfer Sent</option>
              <option value="transfer_in">Transfer Received</option>
            </select>
          </div>
        )}

        {/* Transactions Table / Cards – unchanged */}
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-max">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Number
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Service
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Amount
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Date
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-gray-600 dark:text-gray-400">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-purple-500 rounded-full animate-spin"></div>
                        Loading transaction history...
                      </div>
                    </td>
                  </tr>
                ) : filteredTransactions.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-14 text-center text-gray-600 dark:text-gray-400">
                      No transactions found matching your filters
                    </td>
                  </tr>
                ) : (
                  filteredTransactions.map((t) => (
                    <tr key={t._id || t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        {getNumberValue(t)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {getServiceName(t)}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                        <Currency amount={getAmountValue(t)} />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(t.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span
                          className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusBadge(t.type, t.status)}`}
                        >
                          {getStatusLabel(t)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View – unchanged */}
          <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <div className="p-10 text-center text-gray-600 dark:text-gray-400">
                <div className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-purple-500 rounded-full animate-spin"></div>
                  Loading transaction history...
                </div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-10 text-center text-gray-600 dark:text-gray-400">
                No transactions found matching your filters
              </div>
            ) : (
              filteredTransactions.map((t) => (
                <div key={t._id || t.id} className="p-5">
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <p className="text-base font-semibold text-gray-900 dark:text-white">
                        {getNumberValue(t)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-300 capitalize mt-1">
                        {getServiceName(t)}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full border text-xs font-medium ${getStatusBadge(t.type, t.status)}`}
                    >
                      {getStatusLabel(t)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Amount</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        <Currency amount={getAmountValue(t)} />
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500 dark:text-gray-400">Date</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {formatDate(t.createdAt)}
                      </p>
                    </div>
                  </div>

                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}